import React, { useState, useEffect, } from 'react';
import '../../../../../Stylesheets/Profile/ProfileTab.css';
import { FaUserCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserProfile } from '../../../../../Redux/Auth/authSlice';
import { storage, auth } from '../../../../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { showFeedback } from '../../../../../Redux/UI/uiFeedbackSlice';

const ProfileTab = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    photoURL: '',
  });

  const [hydrated, setHydrated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // ✅ Hydrate once (prevents store updates wiping local edits)
  useEffect(() => {
    if (user && !hydrated) {
      setFormData({
        fullName: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.companyName || '',
        role: user.role || '',
        photoURL: user.photoURL || '',
      });
      setHydrated(true);
    }
  }, [user, hydrated]);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const validatePhone = (digits) => {
    if (!digits) return ''; // allow empty if you want optional phone
    if (digits.length !== 10) return 'Phone number must be exactly 10 digits';
    return '';
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: digits }));
    setPhoneError(validatePhone(digits));
  };

  const handleUpdate = async () => {
    // hard-stop invalid phone
    const phoneMsg = validatePhone(formData.phone);
    if (phoneMsg) {
      setPhoneError(phoneMsg);
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Fix phone number',
          message: phoneMsg,
        })
      );
      return;
    }

    if (!auth.currentUser) {
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Not signed in',
          message: 'Please sign in again to update your profile.',
        })
      );
      return;
    }

    const updates = {
      name: formData.fullName.trim(),
      phone: formData.phone,
      role: formData.role,
      companyName: formData.company.trim(),
    };

    try {
      setIsSaving(true);

      const freshToken = await auth.currentUser.getIdToken(false);

      const action = await dispatch(updateUserProfile({ token: freshToken, updates }));

      if (updateUserProfile.rejected?.match?.(action)) {
        const msg =
          action.payload?.message ||
          action.error?.message ||
          'Failed to update profile.';
        dispatch(showFeedback({ type: 'error', title: 'Update failed', message: msg }));
        return;
      }

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Profile updated',
          message: 'Your changes have been saved.',
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(
        showFeedback({
          type: 'error',
          title: 'Update failed',
          message: 'Failed to update profile.',
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * 🚫 Important: Your previous deleteObject logic was wrong.
   * `ref(storage, downloadURL)` is NOT a valid storage path.
   * If you want deletion, store and persist the storage path like `photoPath`.
   *
   * For now, we avoid deletion and just overwrite the same path.
   * This is simpler and avoids dangling URLs.
   */
  const addCacheBuster = (url) =>
    url + (url.includes("?") ? "&" : "?") + "v=" + Date.now();

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!auth.currentUser) {
      dispatch(showFeedback({ type: "error", title: "Not signed in", message: "Please sign in again." }));
      return;
    }

    try {
      setUploading(true);

      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      });

      const uid = auth.currentUser.uid;
      const fileRef = ref(storage, `profileImages/${uid}/avatar.jpg`);

      await uploadBytes(fileRef, compressedFile);
      const downloadURL = await getDownloadURL(fileRef);

      const cacheBustedURL = addCacheBuster(downloadURL);

      setFormData((prev) => ({ ...prev, photoURL: cacheBustedURL }));

      const freshToken = await auth.currentUser.getIdToken(false);
      await dispatch(updateUserProfile({ token: freshToken, updates: { photoURL: cacheBustedURL } })).unwrap();

      dispatch(showFeedback({ type: "success", title: "Photo updated", message: "Profile photo updated successfully." }));
    } catch (err) {
      dispatch(showFeedback({ type: "error", title: "Upload failed", message: err?.message || "Could not upload photo." }));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };


  return (
    <div className="profile-page-profile-tab">
      <div className="profile-page-profile-info-left">
        <div className="profile-page-profile-field">
          <label>Full Name</label>
          <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} />
        </div>

        <div className="profile-page-profile-row">
          <div className="profile-page-profile-field">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} disabled />
          </div>

          <div className="profile-page-profile-field">
            <label>Phone Number</label>
            <input
              type="tel"
              inputMode="numeric"
              name="phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Enter 10-digit mobile number"
            />
            {phoneError && <span className="input-error">{phoneError}</span>}
          </div>
        </div>

        <div className="profile-page-profile-row">
          <div className="profile-page-profile-field">
            <label>Company</label>
            <input type="text" name="company" value={formData.company} onChange={handleChange} />
          </div>

          {/* <div className="profile-page-profile-field">
            <label>Role</label>
            <select name="role" value={formData.role} onChange={handleChange}>
              <option value="Civil Engineer">Civil Engineer</option>
              <option value="Contractor">Contractor</option>
              <option value="Manager">Manager</option>
            </select>
          </div> */}
        </div>

        <button className="profile-page-edit-btn" onClick={handleUpdate} disabled={isSaving || !!phoneError}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="profile-page-profile-photo-upload">
        <div className="profile-page-upload-box">
          <center>
            {formData.photoURL ? (
              <img
                key={formData.photoURL}
                src={formData.photoURL}
                alt="Profile"
                className="profile-page-uploaded-img"
                onError={(e) => {
                  console.error("Image failed to load:", formData.photoURL);
                  e.currentTarget.style.display = "none";
                  dispatch(showFeedback({
                    type: "error",
                    title: "Image not loading",
                    message: "Profile image URL failed to load (check token/cache).",
                  }));
                }}
              />
            ) : (
              <FaUserCircle className="profile-page-upload-icon" />
            )}


            <label style={uploading ? { opacity: 0.7, pointerEvents: 'none' } : undefined}>
              {uploading ? 'Uploading...' : 'Upload Photo'}
              <input type="file" hidden accept="image/*" onChange={handleImageUpload} disabled={uploading} />
            </label>
          </center>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
